/**
 * VerifyEmailDialog — modal that sends an OTP to the user's email and
 * verifies it. Simplified version of `StepUpDialog`: email-only channel,
 * no channel toggle, no action parameter.
 *
 * Flow: open → `identity.requestEmailVerifyOtp` → email sent → user
 * types 6 digits → auto-submit → `identity.verifyEmailOtp` → stamps
 * `email_verified_at` → `onVerified()` callback → dialog closes.
 *
 * @see step-up-dialog.tsx — the step-up counterpart this is modelled on
 */
import { useEffect, useRef, useState } from "react";
import { Loader2, Mail } from "lucide-react";
import {
  Alert,
  Button,
  DialogRoot,
  DialogContent,
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@flint/ui";
import { trpc } from "../../lib/trpc.ts";

export interface VerifyEmailDialogProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  /** The email address being verified — shown in the dialog body. */
  email: string;
  /** Called once the OTP is verified — the parent invalidates `me`. */
  onVerified: () => void;
}

const COOLDOWN_SECONDS = 60;

export function VerifyEmailDialog({
  open,
  onOpenChange,
  email,
  onVerified,
}: VerifyEmailDialogProps) {
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const lastAttempted = useRef<string | null>(null);
  const [cooldown, setCooldown] = useState(0);

  // Persists the active challengeId across close/reopen so the user can
  // re-enter the already-emailed code without triggering a 429.
  const activeChallengeRef = useRef<string | null>(null);

  // Increments on each request/resend so stale verify rejections from a
  // previous challenge are silently dropped instead of showing a confusing
  // "wrong code" error.
  const requestGeneration = useRef(0);

  const requestOtp = trpc.identity.requestEmailVerifyOtp.useMutation();
  const verifyOtp = trpc.identity.verifyEmailOtp.useMutation();

  // Tick the resend cooldown every second.
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  // On open: reuse the active challenge if one exists (avoids 429 on
  // reopen within cooldown). On close: clear visual state but keep the
  // challengeId + cooldown alive so the next open can reuse them.
  useEffect(() => {
    if (!open) {
      // Clear visual state only — the challenge is still valid on the server.
      setCode("");
      setError(null);
      lastAttempted.current = null;
      return;
    }

    // If we already have a live challenge (e.g. user closed and reopened
    // within the cooldown), restore it and let the user type the code.
    if (activeChallengeRef.current) {
      setChallengeId(activeChallengeRef.current);
      setCode("");
      setError(null);
      return;
    }

    // First open — no existing challenge. Request one.
    let cancelled = false;
    const gen = ++requestGeneration.current;
    setError(null);
    setCode("");
    setChallengeId(null);
    requestOtp
      .mutateAsync({})
      .then((result: { challengeId: string }) => {
        if (cancelled || gen !== requestGeneration.current) return;
        setChallengeId(result.challengeId);
        activeChallengeRef.current = result.challengeId;
        setCooldown(COOLDOWN_SECONDS);
      })
      .catch((err: { message?: string; data?: { cause?: { retryAfterSeconds?: number } } }) => {
        if (cancelled || gen !== requestGeneration.current) return;
        // On 429 (cooldown): seed the client cooldown from the server's
        // retryAfterSeconds so the Resend button counts down correctly.
        // The OTP input stays usable if we have a prior challengeId.
        const retryAfter = err.data?.cause?.retryAfterSeconds;
        if (typeof retryAfter === "number" && retryAfter > 0) {
          setCooldown(retryAfter);
          // If the prior challenge is still valid, keep the input usable.
          if (activeChallengeRef.current) {
            setChallengeId(activeChallengeRef.current);
            return;
          }
        }
        setError(err.message ?? "Could not send code.");
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mutation ref stable
  }, [open]);

  // Auto-submit at 6 digits — same guard as StepUpDialog.
  // Captures the current generation so a resend in-flight discards the
  // stale rejection instead of overwriting the new challenge's state.
  useEffect(() => {
    if (
      open &&
      challengeId &&
      code.length === 6 &&
      code !== lastAttempted.current &&
      !verifyOtp.isPending
    ) {
      lastAttempted.current = code;
      const gen = requestGeneration.current;
      verifyOtp
        .mutateAsync({ challengeId, code })
        .then(() => {
          // Success — clear the persisted challenge so the next open
          // starts fresh.
          activeChallengeRef.current = null;
          onVerified();
          onOpenChange(false);
        })
        .catch(
          (err: {
            message?: string;
            data?: { cause?: { attemptsRemaining?: number } };
          }) => {
            // Stale rejection from a previous challenge — ignore.
            if (gen !== requestGeneration.current) return;

            setCode("");
            lastAttempted.current = null;
            const remaining = err.data?.cause?.attemptsRemaining;
            if (typeof remaining === "number") {
              setError(`Wrong code · ${remaining} attempt${remaining === 1 ? "" : "s"} left.`);
            } else {
              setError(err.message ?? "Verification failed.");
            }
          },
        );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mutation ref stable
  }, [code, challengeId, open]);

  const resend = () => {
    // Guard: don't resend while a verify is in-flight — prevents the stale
    // rejection from overwriting the new challenge's error state.
    if (verifyOtp.isPending) return;

    const gen = ++requestGeneration.current;
    setCode("");
    setError(null);
    lastAttempted.current = null;
    // Keep challengeId usable while the resend is in-flight so the user
    // can still type the old code if the resend fails.
    requestOtp
      .mutateAsync({})
      .then((result: { challengeId: string }) => {
        if (gen !== requestGeneration.current) return;
        setChallengeId(result.challengeId);
        activeChallengeRef.current = result.challengeId;
        setCooldown(COOLDOWN_SECONDS);
      })
      .catch((err: { message?: string; data?: { cause?: { retryAfterSeconds?: number } } }) => {
        if (gen !== requestGeneration.current) return;
        const retryAfter = err.data?.cause?.retryAfterSeconds;
        if (typeof retryAfter === "number" && retryAfter > 0) {
          setCooldown(retryAfter);
          return;
        }
        setError(err.message ?? "Could not send code.");
      });
  };

  return (
    <DialogRoot open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <div className="flex flex-col gap-4">
          <header className="flex items-start gap-3">
            <span
              aria-hidden
              className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-sky-50 text-sky-600"
            >
              <Mail size={16} />
            </span>
            <div className="min-w-0 flex-1">
              <h2 className="text-base font-semibold text-ink m-0">Verify your email</h2>
              <p className="mt-1 text-xs text-muted leading-snug">
                We sent a 6-digit code to{" "}
                <strong className="text-ink-2">{email}</strong>.
                Expires in 5 minutes.
              </p>
            </div>
          </header>

          <div className="flex flex-col gap-3 min-h-[5.5rem]">
            <InputOTP
              maxLength={6}
              autoComplete="one-time-code"
              inputMode="numeric"
              value={code}
              onChange={(next: string) => {
                setCode(next);
                if (error) setError(null);
              }}
              disabled={!challengeId || verifyOtp.isPending}
              autoFocus
            >
              <InputOTPGroup>
                {Array.from({ length: 6 }, (_, i) => (
                  <InputOTPSlot key={i} index={i} />
                ))}
              </InputOTPGroup>
            </InputOTP>

            {requestOtp.isPending && !challengeId ? (
              <p className="inline-flex items-center gap-2 text-xs text-muted">
                <Loader2 size={12} className="animate-spin" />
                Sending code…
              </p>
            ) : null}

            {verifyOtp.isPending ? (
              <p className="inline-flex items-center gap-2 text-xs text-muted">
                <Loader2 size={12} className="animate-spin" />
                Verifying…
              </p>
            ) : null}

            {error ? <Alert intent="bad" title="Couldn't verify" description={error} /> : null}
          </div>

          <footer className="flex items-center justify-between gap-2 pt-2 border-t border-line">
            <button
              type="button"
              onClick={resend}
              disabled={requestOtp.isPending || verifyOtp.isPending || cooldown > 0}
              className="text-xs text-sky-700 font-semibold hover:underline disabled:opacity-50"
            >
              {cooldown > 0 ? `Resend code (${cooldown}s)` : "Resend code"}
            </button>
            <Button type="button" intent="ghost" size="sm" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
          </footer>
        </div>
      </DialogContent>
    </DialogRoot>
  );
}
